import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Trash2, AlertTriangle, X, Tag, Plus } from 'lucide-react'
import {
  listQualifications,
  listBackgrounds,
  listSubjects,
  exportMatrix,
  deleteQualification,
  createQualification,
  updateQualification,
  createBackground,
  createSubject,
  setSubjectQualificationOverride,
  setSubjectSubstitutionExcluded,
  bulkDeleteMatrix,
  bulkDeleteSelectedHeaders,
  deleteBackground,
  deleteSubject,
  listSubjectAliases,
  listBackgroundAliases,
  createSubjectAlias,
  createBackgroundAlias,
  deleteSubjectAlias,
  deleteBackgroundAlias,
  listQualificationGroups,
  createQualificationGroup,
  updateQualificationGroup,
  deleteQualificationGroup,
  removeGroupMember,
} from '@/services/api/qualification'
import type {
  QualificationEntry,
  Background,
  Subject,
  ImportResult,
  SubjectQualificationGroup,
} from '@/types/qualification'
import { getQualificationColor, groupChipLabel, groupChoicesForSubject, nextGroupNameForSubject, occupiedGroupNamesForSubject } from '@/types/qualification'
import { QualificationMatrixImport } from '@/components/qualification/QualificationMatrixImport'
import { computeCellPanelBox, measuredPanelNeed } from '@/components/qualification/cellPanelBox'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'

type Tab = 'matrix' | 'aliases' | 'import'

type CellCombo = { name: string; mode: 'or' | 'and'; memberBgIds: string[] }
type CellDraft = { combos: CellCombo[]; activeIndex: number; qualified: boolean }

function toggleSetId(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

export const QualificationMatrixPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('matrix')
  const [entries, setEntries] = useState<QualificationEntry[]>([])
  const [backgrounds, setBackgrounds] = useState<Background[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [groups, setGroups] = useState<SubjectQualificationGroup[]>([])
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false)
  const [editingCell, setEditingCell] = useState<{ bgId: string; subjId: string } | null>(null)
  const [cellDraft, setCellDraft] = useState<CellDraft | null>(null)
  const [cellDraftBaseline, setCellDraftBaseline] = useState<string>('')
  const [savingCell, setSavingCell] = useState(false)
  const [cellPanelPos, setCellPanelPos] = useState({ top: 0, left: 0, width: 288, maxHeight: 420 })
  const cellAnchorRef = useRef<HTMLElement | null>(null)
  const cellPanelRef = useRef<HTMLElement | null>(null)
  const [newSubjectMode, setNewSubjectMode] = useState(false)
  const [newSubjectName, setNewSubjectName] = useState('')
  const [newBackgroundMode, setNewBackgroundMode] = useState(false)
  const [newBackgroundName, setNewBackgroundName] = useState('')
  const [deletingSubjectId, setDeletingSubjectId] = useState<string | null>(null)
  const [deletingBackgroundId, setDeletingBackgroundId] = useState<string | null>(null)
  const [showAliasDialog, setShowAliasDialog] = useState<{ type: 'subject' | 'background'; id: string; name: string; code: string | null } | null>(null)
  const [subjectAliases, setSubjectAliases] = useState<Array<{ id: string; alias_name: string; canonical_subject_id: string; source_type: string; is_confirmed: boolean }>>([])
  const [backgroundAliases, setBackgroundAliases] = useState<Array<{ id: string; alias_name: string; canonical_background_id: string; source_type: string; is_confirmed: boolean }>>([])
  const [allSubjectAliases, setAllSubjectAliases] = useState<Array<{ id: string; alias_name: string; canonical_subject_id: string; source_type: string; is_confirmed: boolean; canonical_subject_name?: string }>>([])
  const [allBackgroundAliases, setAllBackgroundAliases] = useState<Array<{ id: string; alias_name: string; canonical_background_id: string; source_type: string; is_confirmed: boolean; canonical_background_name?: string }>>([])
  const [newAliasMode, setNewAliasMode] = useState<'subject' | 'background' | null>(null)
  const [newAliasName, setNewAliasName] = useState('')
  const [newAliasEntityId, setNewAliasEntityId] = useState('')
  const [aliasFilter, setAliasFilter] = useState('')
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<Set<string>>(new Set())
  const [selectedBackgroundIds, setSelectedBackgroundIds] = useState<Set<string>>(new Set())
  const [selectedSubjectAliasIds, setSelectedSubjectAliasIds] = useState<Set<string>>(new Set())
  const [selectedBackgroundAliasIds, setSelectedBackgroundAliasIds] = useState<Set<string>>(new Set())
  const [deletingSelected, setDeletingSelected] = useState(false)
  const [unmatchedOnly, setUnmatchedOnly] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [entriesData, bgData, subjData, groupsData] = await Promise.all([
        listQualifications(),
        listBackgrounds(false, true),
        listSubjects(false, true),
        listQualificationGroups(),
      ])
      setEntries(entriesData)
      setBackgrounds(bgData)
      setSubjects(subjData)
      const liveGroups = groupsData.groups.filter(g => g.members.length > 0)
      setGroups(liveGroups)
      setSelectedSubjectIds(prev => new Set([...prev].filter(id => subjData.some(s => s.id === id))))
      setSelectedBackgroundIds(prev => new Set([...prev].filter(id => bgData.some(b => b.id === id))))
      return { entries: entriesData, groups: liveGroups }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Qualifikationsdaten konnten nicht geladen werden',
      })
      console.error('Failed to load qualification data:', err)
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleExport = async (format: 'xlsx' | 'csv') => {
    try {
      const blob = await exportMatrix(format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `qualifications.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export qualifications:', err)
    }
  }

  const closeCellEditorSilent = useCallback(() => {
    cellAnchorRef.current = null
    setEditingCell(null)
    setCellDraft(null)
    setCellDraftBaseline('')
  }, [])

  const handleDelete = useCallback(async (
    bgId: string,
    bgName: string | null,
    subjId: string,
    subjName: string | null,
    entry?: QualificationEntry,
  ) => {
    if (!confirm(`${bgName || 'Hintergrund'} → ${subjName || 'Fach'} löschen?`)) return
    try {
      const cellGroups = groups.filter(
        g => g.subject_id === subjId && g.members.some(m => m.background_id === bgId),
      )
      for (const g of cellGroups) {
        await removeGroupMember(g.id, bgId)
      }
      if (entry) {
        await deleteQualification(entry.id)
      }
      if (editingCell?.bgId === bgId && editingCell?.subjId === subjId) {
        closeCellEditorSilent()
      }
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Qualifikation konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    }
  }, [closeCellEditorSilent, editingCell, groups, loadData])

  const handleDeleteCellGroup = useCallback(async (
    group: SubjectQualificationGroup,
    bgId: string,
    subjId: string,
    entry?: QualificationEntry,
  ) => {
    if (!confirm(`Gruppe «${group.name}» löschen?`)) return
    try {
      await removeGroupMember(group.id, bgId)
      const remaining = groups.filter(
        g => g.id !== group.id
          && g.subject_id === subjId
          && g.members.some(m => m.background_id === bgId),
      )
      if (remaining.length === 0 && entry?.qualification_type === 'group') {
        await deleteQualification(entry.id)
      } else if (remaining.length > 0 && entry?.qualification_type === 'group' && entry.group_name !== remaining[0]?.name) {
        await updateQualification(entry.id, {
          qualification_type: 'group',
          group_name: remaining[0]?.name,
        })
      }
      if (editingCell?.bgId === bgId && editingCell?.subjId === subjId) {
        closeCellEditorSilent()
      }
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Gruppe konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    }
  }, [closeCellEditorSilent, editingCell, groups, loadData])

  const handleDeleteAll = useCallback(async () => {
    try {
      const result = await bulkDeleteMatrix(true)
      setShowDeleteAllDialog(false)
      setError(null)
      loadData()
      const parts: string[] = []
      if (result.entries_deleted > 0) parts.push(`${result.entries_deleted} Einträge`)
      if (result.groups_deleted > 0) parts.push(`${result.groups_deleted} Gruppen`)
      if (result.backgrounds_deleted > 0) parts.push(`${result.backgrounds_deleted} Hintergründe`)
      if (result.subjects_deleted > 0) parts.push(`${result.subjects_deleted} Fächer`)
      if (result.aliases_deleted > 0) parts.push(`${result.aliases_deleted} Aliase`)
      if (parts.length > 0) {
        alert(`${parts.join(', ')} gelöscht.`)
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Matrix konnte nicht gelöscht werden',
      })
      setError(errorMessage)
      setShowDeleteAllDialog(false)
    }
  }, [loadData])

  const buildCellDraft = useCallback((
    bgId: string,
    subjId: string,
    extraUsedNames: string[] = [],
    snapshot?: {
      groups?: SubjectQualificationGroup[]
      entries?: QualificationEntry[]
      savedDraft?: CellDraft | null
    },
  ): CellDraft => {
    const groupsNow = snapshot?.groups ?? groups
    const entriesNow = snapshot?.entries ?? entries
    const subj = subjects.find(s => s.id === subjId)
    const myGroups = groupsNow.filter(
      g => g.subject_id === subjId && g.members.some(m => m.background_id === bgId),
    )
    let combos: CellCombo[] = myGroups.map(g => ({
      name: g.name,
      mode: g.match_mode || 'or',
      memberBgIds: g.members.map(m => m.background_id),
    }))
    const savedHits = snapshot?.savedDraft && !snapshot.savedDraft.qualified
      ? snapshot.savedDraft.combos.filter(c => c.memberBgIds.includes(bgId))
      : []
    if (combos.length === 0 && savedHits.length > 0) {
      combos = savedHits.map(c => ({ ...c, memberBgIds: [...c.memberBgIds] }))
    }
    const entry = entriesNow.find(e => e.background_id === bgId && e.subject_id === subjId)
    const qualified = entry?.qualification_type === 'qualified' && combos.length === 0
    if (combos.length === 0 && !qualified) {
      const used = occupiedGroupNamesForSubject(
        groupsNow.filter(g => g.subject_id === subjId),
        extraUsedNames,
      )
      const name = nextGroupNameForSubject(subj?.name || 'Fach', used)
      combos.push({ name, mode: 'or', memberBgIds: [bgId] })
    }
    return { combos, activeIndex: 0, qualified }
  }, [entries, groups, subjects])

  const cellPanelDirty = Boolean(cellDraft && JSON.stringify(cellDraft) !== cellDraftBaseline)

  const placeCellPanel = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    const measured = cellPanelRef.current ? measuredPanelNeed(cellPanelRef.current) : { width: 0, height: 0 }
    const next = computeCellPanelBox({
      anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      panel: measured,
    })
    setCellPanelPos(prev => (
      prev.top === next.top
      && prev.left === next.left
      && prev.width === next.width
      && prev.maxHeight === next.maxHeight
        ? prev
        : next
    ))
  }, [])

  const persistCellDraft = useCallback(async (
    cell: { bgId: string; subjId: string },
    draft: CellDraft,
  ): Promise<boolean> => {
    const { bgId, subjId } = cell
    const entry = entries.find(e => e.background_id === bgId && e.subject_id === subjId)
    try {
      if (draft.qualified) {
        const cellGroups = groups.filter(
          g => g.subject_id === subjId && g.members.some(m => m.background_id === bgId),
        )
        for (const g of cellGroups) {
          await removeGroupMember(g.id, bgId)
        }
        if (entry) {
          await updateQualification(entry.id, {
            qualification_type: 'qualified',
            group_name: null,
          })
        } else {
          await createQualification({
            background_id: bgId,
            subject_id: subjId,
            qualification_type: 'qualified',
            group_name: null,
          })
        }
        return true
      }
      const comboNames = new Set(draft.combos.map(c => c.name))
      const knownByBg = new Map<string, { id?: string; qualification_type?: string; group_name?: string | null }>()
      for (const e of entries) {
        if (e.subject_id === subjId) knownByBg.set(e.background_id, e)
      }
      const retargetOrClearMember = async (memberBgId: string) => {
        const memberEntry = knownByBg.get(memberBgId)
        if (!memberEntry) return
        const stillInDraft = draft.combos.filter(c => c.memberBgIds.includes(memberBgId))
        if (stillInDraft.length > 0) {
          const keepName = stillInDraft[0]?.name
          if (memberEntry.id && (memberEntry.qualification_type !== 'group' || memberEntry.group_name !== keepName)) {
            await updateQualification(memberEntry.id, {
              qualification_type: 'group',
              group_name: keepName,
            })
          }
          return
        }
        const otherGroups = groups.filter(g =>
          g.subject_id === subjId
          && !comboNames.has(g.name)
          && g.members.some(m => m.background_id === memberBgId),
        )
        if (!memberEntry.id) return
        if (otherGroups.length > 0) {
          await updateQualification(memberEntry.id, {
            qualification_type: 'group',
            group_name: otherGroups[0]?.name,
          })
        } else if (memberEntry.qualification_type === 'group') {
          await deleteQualification(memberEntry.id)
        }
      }
      for (const g of groups.filter(g => g.subject_id === subjId && g.members.length === 0)) {
        await deleteQualificationGroup(g.id)
      }
      for (const combo of draft.combos) {
        const mode = combo.memberBgIds.length < 2 ? 'or' : combo.mode
        const existing = groups.find(g => g.subject_id === subjId && g.name === combo.name)
        const previousIds = existing ? existing.members.map(m => m.background_id) : []
        if (!existing) {
          await createQualificationGroup({
            subject_id: subjId,
            name: combo.name,
            member_background_ids: combo.memberBgIds,
            match_mode: mode,
          })
        } else {
          await updateQualificationGroup(existing.id, {
            match_mode: mode,
            member_background_ids: combo.memberBgIds,
          })
        }
        for (const memberBgId of combo.memberBgIds) {
          const keepName = draft.combos.find(c => c.memberBgIds.includes(memberBgId))?.name ?? combo.name
          const memberEntry = knownByBg.get(memberBgId)
          if (!memberEntry) {
            const created = await createQualification({
              background_id: memberBgId,
              subject_id: subjId,
              qualification_type: 'group',
              group_name: keepName,
            })
            knownByBg.set(memberBgId, {
              ...created,
              qualification_type: 'group',
              group_name: keepName,
            })
          } else if (
            memberEntry.qualification_type !== 'group'
            || memberEntry.group_name !== keepName
          ) {
            if (memberEntry.id) {
              await updateQualification(memberEntry.id, {
                qualification_type: 'group',
                group_name: keepName,
              })
            }
            knownByBg.set(memberBgId, {
              ...memberEntry,
              qualification_type: 'group',
              group_name: keepName,
            })
          }
        }
        for (const prevId of previousIds) {
          if (combo.memberBgIds.includes(prevId)) continue
          await retargetOrClearMember(prevId)
        }
      }
      for (const g of groups.filter(g => g.subject_id === subjId && g.members.some(m => m.background_id === bgId))) {
        if (!comboNames.has(g.name)) await removeGroupMember(g.id, bgId)
      }
      for (const combo of draft.combos) {
        for (const memberBgId of combo.memberBgIds) {
          if (memberBgId === bgId) continue
          for (const g of groups.filter(
            g => g.subject_id === subjId && g.members.some(m => m.background_id === memberBgId),
          )) {
            if (comboNames.has(g.name)) continue
            if (g.members.length > 1) continue
            await removeGroupMember(g.id, memberBgId)
          }
        }
      }
      if (draft.combos.length === 0 && entry) {
        await deleteQualification(entry.id)
      }
      return true
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Zelle konnte nicht gespeichert werden',
      })
      setError(errorMessage)
      return false
    }
  }, [entries, groups])

  const openCellEditor = useCallback(async (bgId: string, subjId: string, anchor: HTMLElement) => {
    if (savingCell) return
    const cell = (anchor.closest('td') as HTMLElement | null) || anchor
    if (editingCell?.bgId === bgId && editingCell?.subjId === subjId) {
      cellAnchorRef.current = cell
      placeCellPanel(cell)
      return
    }
    let extraUsed: string[] = []
    let snapshot: {
      groups?: SubjectQualificationGroup[]
      entries?: QualificationEntry[]
      savedDraft?: CellDraft | null
    } | undefined
    if (editingCell && cellDraft && cellPanelDirty) {
      setSavingCell(true)
      try {
        const ok = await persistCellDraft(editingCell, cellDraft)
        if (!ok) return
        extraUsed = cellDraft.combos.map(c => c.name)
        const fresh = await loadData()
        snapshot = {
          groups: fresh?.groups,
          entries: fresh?.entries,
          savedDraft: cellDraft,
        }
      } finally {
        setSavingCell(false)
      }
    }
    cellAnchorRef.current = cell
    const draft = buildCellDraft(bgId, subjId, extraUsed, snapshot)
    const groupsNow = snapshot?.groups ?? groups
    const entriesNow = snapshot?.entries ?? entries
    const hadData = groupsNow.some(
      g => g.subject_id === subjId && g.members.some(m => m.background_id === bgId),
    ) || entriesNow.some(e => e.background_id === bgId && e.subject_id === subjId)
      || Boolean(snapshot?.savedDraft?.combos.some(c => c.memberBgIds.includes(bgId)))
    setEditingCell({ bgId, subjId })
    setCellDraft(draft)
    setCellDraftBaseline(hadData ? JSON.stringify(draft) : JSON.stringify({ ...draft, combos: [] }))
    placeCellPanel(cell)
  }, [buildCellDraft, cellDraft, cellPanelDirty, editingCell, entries, groups, loadData, persistCellDraft, placeCellPanel, savingCell])

  const closeCellEditor = useCallback(() => {
    if (cellPanelDirty && !confirm('Ungespeicherte Änderungen verwerfen?')) return
    cellAnchorRef.current = null
    setEditingCell(null)
    setCellDraft(null)
    setCellDraftBaseline('')
  }, [cellPanelDirty])

  useEffect(() => {
    if (!editingCell) return
    const update = () => {
      if (cellAnchorRef.current) placeCellPanel(cellAnchorRef.current)
    }
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [editingCell, placeCellPanel])

  useLayoutEffect(() => {
    if (!editingCell || !cellAnchorRef.current) return
    placeCellPanel(cellAnchorRef.current)
    const panel = cellPanelRef.current
    if (!panel || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (cellAnchorRef.current) placeCellPanel(cellAnchorRef.current)
    })
    ro.observe(panel)
    const body = panel.querySelector('[data-cell-panel-body]')
    if (body) ro.observe(body)
    return () => ro.disconnect()
  }, [editingCell, cellDraft, placeCellPanel])

  const applyCellPanel = useCallback(async () => {
    if (!editingCell || !cellDraft) return
    setSavingCell(true)
    try {
      const ok = await persistCellDraft(editingCell, cellDraft)
      if (!ok) return
      cellAnchorRef.current = null
      setEditingCell(null)
      setCellDraft(null)
      setCellDraftBaseline('')
      loadData()
    } finally {
      setSavingCell(false)
    }
  }, [cellDraft, editingCell, loadData, persistCellDraft])

  const handleCreateSubject = useCallback(async () => {
    if (!newSubjectName.trim()) return
    try {
      await createSubject({ name: newSubjectName.trim() }, true)
      setNewSubjectName('')
      setNewSubjectMode(false)
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Subject konnte nicht erstellt werden',
      })
      setError(errorMessage)
    }
  }, [newSubjectName, loadData])

  const handleCreateBackground = useCallback(async () => {
    if (!newBackgroundName.trim()) return
    try {
      await createBackground({ name: newBackgroundName.trim() }, true)
      setNewBackgroundName('')
      setNewBackgroundMode(false)
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Background konnte nicht erstellt werden',
      })
      setError(errorMessage)
    }
  }, [newBackgroundName, loadData])

  const handleDeleteSubject = useCallback(async (subjId: string, subjName: string) => {
    if (!confirm(`Subject "${subjName}" löschen?`)) return
    setDeletingSubjectId(subjId)
    try {
      await deleteSubject(subjId)
      setSelectedSubjectIds(prev => {
        const next = new Set(prev)
        next.delete(subjId)
        return next
      })
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Subject konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    } finally {
      setDeletingSubjectId(null)
    }
  }, [loadData])

  const handleDeleteBackground = useCallback(async (bgId: string, bgName: string) => {
    if (!confirm(`Background "${bgName}" löschen?`)) return
    setDeletingBackgroundId(bgId)
    try {
      await deleteBackground(bgId)
      setSelectedBackgroundIds(prev => {
        const next = new Set(prev)
        next.delete(bgId)
        return next
      })
      loadData()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Background konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    } finally {
      setDeletingBackgroundId(null)
    }
  }, [loadData])

  const handleShowAliases = useCallback(async (type: 'subject' | 'background', id: string, name: string, code: string | null) => {
    setShowAliasDialog({ type, id, name, code })
    try {
      if (type === 'subject') {
        const aliases = await listSubjectAliases()
        setSubjectAliases(aliases.filter(a => a.canonical_subject_id === id))
      } else {
        const aliases = await listBackgroundAliases()
        setBackgroundAliases(aliases.filter(a => a.canonical_background_id === id))
      }
    } catch (err) {
      console.error('Failed to load aliases:', err)
    }
  }, [])

  const loadAllAliases = useCallback(async () => {
    try {
      const [subjAliases, bgAliases] = await Promise.all([
        listSubjectAliases(),
        listBackgroundAliases(),
      ])
      // Enrich with names
      const subjMap = new Map(subjects.map(s => [s.id, s.name]))
      const bgMap = new Map(backgrounds.map(b => [b.id, b.name]))
      const nextSubjectAliases = subjAliases.map(a => ({
        ...a,
        canonical_subject_name: subjMap.get(a.canonical_subject_id) || 'Unbekannt',
      }))
      const nextBackgroundAliases = bgAliases.map(a => ({
        ...a,
        canonical_background_name: bgMap.get(a.canonical_background_id) || 'Unbekannt',
      }))
      setAllSubjectAliases(nextSubjectAliases)
      setAllBackgroundAliases(nextBackgroundAliases)
      setSelectedSubjectAliasIds(prev => new Set([...prev].filter(id => nextSubjectAliases.some(a => a.id === id))))
      setSelectedBackgroundAliasIds(prev => new Set([...prev].filter(id => nextBackgroundAliases.some(a => a.id === id))))
    } catch (err) {
      console.error('Failed to load aliases:', err)
      setError('Aliase konnten nicht geladen werden')
    }
  }, [subjects, backgrounds])

  useEffect(() => {
    if (activeTab === 'aliases') {
      void loadAllAliases()
    }
  }, [activeTab, loadAllAliases])

  const handleCreateSubjectAlias = useCallback(async () => {
    if (!newAliasName.trim() || !newAliasEntityId) return
    try {
      await createSubjectAlias({
        canonical_subject_id: newAliasEntityId,
        alias_name: newAliasName.trim(),
        source_type: 'manual',
        is_confirmed: true,
      })
      setNewAliasName('')
      setNewAliasEntityId('')
      setNewAliasMode(null)
      await loadAllAliases()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Alias konnte nicht erstellt werden',
      })
      setError(errorMessage)
    }
  }, [newAliasName, newAliasEntityId, loadAllAliases])

  const handleCreateBackgroundAlias = useCallback(async () => {
    if (!newAliasName.trim() || !newAliasEntityId) return
    try {
      await createBackgroundAlias({
        canonical_subject_id: newAliasEntityId,
        alias_name: newAliasName.trim(),
        source_type: 'manual',
        is_confirmed: true,
      })
      setNewAliasName('')
      setNewAliasEntityId('')
      setNewAliasMode(null)
      await loadAllAliases()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Alias konnte nicht erstellt werden',
      })
      setError(errorMessage)
    }
  }, [newAliasName, newAliasEntityId, loadAllAliases])

  const handleDeleteSubjectAlias = useCallback(async (aliasId: string) => {
    if (!confirm('Alias wirklich löschen?')) return
    try {
      await deleteSubjectAlias(aliasId)
      await loadAllAliases()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Alias konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    }
  }, [loadAllAliases])

  const handleDeleteBackgroundAlias = useCallback(async (aliasId: string) => {
    if (!confirm('Alias wirklich löschen?')) return
    try {
      await deleteBackgroundAlias(aliasId)
      await loadAllAliases()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Alias konnte nicht gelöscht werden',
      })
      setError(errorMessage)
    }
  }, [loadAllAliases])

  const handleDeleteSelectedHeaders = useCallback(async () => {
    const backgroundIds = [...selectedBackgroundIds]
    const subjectIds = [...selectedSubjectIds]
    if (backgroundIds.length === 0 && subjectIds.length === 0) return
    const parts: string[] = []
    if (backgroundIds.length > 0) {
      parts.push(`${backgroundIds.length} ${backgroundIds.length === 1 ? 'Hintergrund' : 'Hintergründe'}`)
    }
    if (subjectIds.length > 0) {
      parts.push(`${subjectIds.length} ${subjectIds.length === 1 ? 'Fach' : 'Fächer'}`)
    }
    if (!confirm(
      `${parts.join(' und ')} löschen? Zugehörige Einträge, Gruppen und Aliase werden mitgelöscht.`,
    )) return
    setDeletingSelected(true)
    try {
      const result = await bulkDeleteSelectedHeaders(backgroundIds, subjectIds)
      setSelectedBackgroundIds(new Set())
      setSelectedSubjectIds(new Set())
      setError(null)
      await loadData()
      const summary: string[] = []
      if (result.backgrounds_deleted > 0) summary.push(`${result.backgrounds_deleted} Hintergründe`)
      if (result.subjects_deleted > 0) summary.push(`${result.subjects_deleted} Fächer`)
      if (result.entries_deleted > 0) summary.push(`${result.entries_deleted} Einträge`)
      if (result.groups_deleted > 0) summary.push(`${result.groups_deleted} Gruppen`)
      if (result.aliases_deleted > 0) summary.push(`${result.aliases_deleted} Aliase`)
      if (summary.length > 0) {
        alert(`${summary.join(', ')} gelöscht.`)
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Auswahl konnte nicht gelöscht werden',
      })
      setError(errorMessage)
      await loadData()
    } finally {
      setDeletingSelected(false)
    }
  }, [selectedBackgroundIds, selectedSubjectIds, loadData])

  const handleDeleteSelectedAliases = useCallback(async () => {
    const subjectAliasIds = [...selectedSubjectAliasIds]
    const backgroundAliasIds = [...selectedBackgroundAliasIds]
    const n = subjectAliasIds.length + backgroundAliasIds.length
    if (n === 0) return
    if (!confirm(`${n} ${n === 1 ? 'Alias' : 'Aliase'} löschen?`)) return
    setDeletingSelected(true)
    let deleted = 0
    let failed = 0
    try {
      for (const id of subjectAliasIds) {
        try {
          await deleteSubjectAlias(id)
          deleted += 1
        } catch {
          failed += 1
        }
      }
      for (const id of backgroundAliasIds) {
        try {
          await deleteBackgroundAlias(id)
          deleted += 1
        } catch {
          failed += 1
        }
      }
      setSelectedSubjectAliasIds(new Set())
      setSelectedBackgroundAliasIds(new Set())
      if (failed > 0) {
        setError(`${deleted} Aliase gelöscht, ${failed} fehlgeschlagen`)
      } else {
        setError(null)
      }
      await loadAllAliases()
    } finally {
      setDeletingSelected(false)
    }
  }, [selectedSubjectAliasIds, selectedBackgroundAliasIds, loadAllAliases])

  const matrix = new Map<string, Map<string, QualificationEntry>>()
  for (const entry of entries) {
    if (!matrix.has(entry.background_id)) {
      matrix.set(entry.background_id, new Map())
    }
    matrix.get(entry.background_id)!.set(entry.subject_id, entry)
  }

  const groupsBySubject = new Map<string, SubjectQualificationGroup[]>()
  for (const g of groups) {
    if (!groupsBySubject.has(g.subject_id)) {
      groupsBySubject.set(g.subject_id, [])
    }
    groupsBySubject.get(g.subject_id)!.push(g)
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'matrix', label: 'Matrix' },
    { key: 'aliases', label: 'Aliase' },
    { key: 'import', label: 'Import' },
  ]

  const hasMatrixData = entries.length > 0 || backgrounds.length > 0 || subjects.length > 0
  const unmatchedBackgrounds = backgrounds.filter(b => b.teacher_count === 0)
  const visibleBackgrounds = unmatchedOnly && unmatchedBackgrounds.length > 0
    ? unmatchedBackgrounds
    : backgrounds
  const unmatchedLabel = unmatchedBackgrounds.length === 1
    ? '1 Hintergrund ohne Match'
    : `${unmatchedBackgrounds.length} Hintergründe ohne Match`
  const jumpToBackground = (id: string) => {
    document.getElementById(`matrix-bg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  const allHeadersSelected =
    (subjects.length === 0 || subjects.every(s => selectedSubjectIds.has(s.id))) &&
    (visibleBackgrounds.length === 0 || visibleBackgrounds.every(b => selectedBackgroundIds.has(b.id))) &&
    (subjects.length > 0 || visibleBackgrounds.length > 0)
  const someHeadersSelected = selectedSubjectIds.size > 0 || selectedBackgroundIds.size > 0
  const filterNeedle = aliasFilter.toLowerCase()
  const filteredSubjectAliases = allSubjectAliases
    .filter(a =>
      !filterNeedle ||
      a.alias_name.toLowerCase().includes(filterNeedle) ||
      (a.canonical_subject_name || '').toLowerCase().includes(filterNeedle)
    )
    .sort((a, b) => (a.canonical_subject_name || '').localeCompare(b.canonical_subject_name || ''))
  const filteredBackgroundAliases = allBackgroundAliases
    .filter(a =>
      !filterNeedle ||
      a.alias_name.toLowerCase().includes(filterNeedle) ||
      (a.canonical_background_name || '').toLowerCase().includes(filterNeedle)
    )
    .sort((a, b) => (a.canonical_background_name || '').localeCompare(b.canonical_background_name || ''))
  const allVisibleSubjectAliasesSelected =
    filteredSubjectAliases.length > 0 && filteredSubjectAliases.every(a => selectedSubjectAliasIds.has(a.id))
  const allVisibleBackgroundAliasesSelected =
    filteredBackgroundAliases.length > 0 && filteredBackgroundAliases.every(a => selectedBackgroundAliasIds.has(a.id))
  const selectedAliasCount = selectedSubjectAliasIds.size + selectedBackgroundAliasIds.size
  const visibleAliasCount = filteredSubjectAliases.length + filteredBackgroundAliases.length
  const allVisibleAliasesSelected =
    visibleAliasCount > 0 &&
    (filteredSubjectAliases.length === 0 || allVisibleSubjectAliasesSelected) &&
    (filteredBackgroundAliases.length === 0 || allVisibleBackgroundAliasesSelected)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <h1 className="text-2xl font-bold">Qualifikations-Matrix</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {activeTab === 'matrix' && unmatchedBackgrounds.length > 0 && (
            <span className="px-2.5 py-1 bg-gray-900 text-white text-xs rounded-full">
              {unmatchedBackgrounds.length} ohne Match
            </span>
          )}
          <div className="flex gap-2">
            <button
              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              onClick={() => handleExport('xlsx')}
            >
              Export Excel
            </button>
            <button
              className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
              onClick={() => handleExport('csv')}
            >
              Export CSV
            </button>
          </div>
          <button
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            onClick={() => setActiveTab('import')}
          >
            Import
          </button>
          <button
            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowDeleteAllDialog(true)}
            disabled={!hasMatrixData}
          >
            <Trash2 className="w-3 h-3" />
            Matrix löschen
          </button>
          <Link
            to="/superadmin/settings/teacher-params"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            Backgrounds & Subjects verwalten →
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">&times;</button>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`px-4 py-2 border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && !entries.length && !groups.length && !backgrounds.length && !subjects.length ? (
        <div className="text-center py-12 text-gray-500">Laden...</div>
      ) : (
        <>
          {activeTab === 'matrix' && (
            entries.length > 0 || backgrounds.length > 0 || subjects.length > 0 ? (
              <div>
                {unmatchedBackgrounds.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>
                      <strong>{unmatchedLabel}</strong>
                      {' '}— kein Lehrer mit diesem Hintergrund.
                    </span>
                    {unmatchedBackgrounds.map(b => (
                      <button
                        key={b.id}
                        type="button"
                        className="bg-white border border-red-200 text-red-700 rounded-full px-2.5 py-0.5 text-xs font-bold hover:bg-red-50"
                        onClick={() => jumpToBackground(b.id)}
                        aria-label={`Zu ${b.name} springen`}
                      >
                        {b.name}
                      </button>
                    ))}
                    <label className="ml-auto flex items-center gap-1.5 text-gray-800 text-xs">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600"
                        checked={unmatchedOnly}
                        onChange={e => setUnmatchedOnly(e.target.checked)}
                      />
                      Nur ohne Match
                    </label>
                  </div>
                )}
                {someHeadersSelected && (
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="text-sm text-gray-600">
                      {selectedBackgroundIds.size} Hintergründe, {selectedSubjectIds.size} Fächer ausgewählt
                    </span>
                    <button
                      className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1 disabled:opacity-50"
                      onClick={handleDeleteSelectedHeaders}
                      disabled={deletingSelected}
                    >
                      <Trash2 className="w-3 h-3" />
                      Ausgewählte löschen
                    </button>
                    <button
                      className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
                      onClick={() => {
                        setSelectedSubjectIds(new Set())
                        setSelectedBackgroundIds(new Set())
                      }}
                    >
                      Auswahl aufheben
                    </button>
                  </div>
                )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 bg-gray-100 border text-left sticky left-0 bg-gray-100 z-10 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600"
                            checked={allHeadersSelected}
                            ref={el => {
                              if (el) el.indeterminate = someHeadersSelected && !allHeadersSelected
                            }}
                            onChange={() => {
                              if (allHeadersSelected) {
                                setSelectedSubjectIds(new Set())
                                setSelectedBackgroundIds(new Set())
                              } else {
                                setSelectedSubjectIds(new Set(subjects.map(s => s.id)))
                                 setSelectedBackgroundIds(new Set(visibleBackgrounds.map(b => b.id)))
                              }
                            }}
                            title="Alle Hintergründe und Fächer auswählen"
                            aria-label="Alle Hintergründe und Fächer auswählen"
                          />
                          <span>Background / Subject</span>
                        </div>
                      </th>
                      {subjects.map((subj) => (
                        <th key={subj.id} className="p-2 bg-gray-100 border text-center min-w-[100px] group">
                          <div className="flex flex-col items-stretch gap-1 min-w-0">
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600"
                              checked={selectedSubjectIds.has(subj.id)}
                              onChange={() => setSelectedSubjectIds(prev => toggleSetId(prev, subj.id))}
                              onClick={e => e.stopPropagation()}
                              title={`${subj.name} auswählen`}
                              aria-label={`${subj.name} auswählen`}
                            />
                            <span
                              className="cursor-pointer hover:text-blue-600 flex items-center gap-1"
                              onClick={() => handleShowAliases('subject', subj.id, subj.name, subj.code)}
                              title="Aliase und Code anzeigen"
                            >
                              {subj.name}
                              {(subj.code || true) && <Tag className="w-3 h-3 text-gray-400" />}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSubject(subj.id, subj.name)
                              }}
                              disabled={deletingSubjectId === subj.id}
                              className="p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-100"
                              title="Subject löschen"
                            >
                              {deletingSubjectId === subj.id ? (
                                <span className="inline-block w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <X className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                          <label className="flex items-center justify-center gap-1 max-w-[6.5rem] mx-auto text-[10px] leading-tight text-gray-700 whitespace-normal text-center" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={!!subj.substitution_excluded}
                              onChange={e => {
                                const excluded = e.target.checked
                                void setSubjectSubstitutionExcluded(subj.id, excluded)
                                  .then(updated => {
                                    setSubjects(prev => prev.map(row => row.id === subj.id ? { ...row, ...updated } : row))
                                  })
                                  .catch((err: unknown) => {
                                    setError(extractErrorMessage(err, { loadError: 'Einstellung konnte nicht gespeichert werden' }))
                                  })
                              }}
                              aria-label={`${subj.name} ohne Stellvertretung`}
                            />
                            ohne Stellvertretung
                          </label>
                          <div className="w-[6.5rem] max-w-full mx-auto overflow-hidden">
                          <select
                            value={subj.qualification_override ?? 'cells'}
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              const override = e.target.value as 'cells' | 'all' | 'none'
                              void setSubjectQualificationOverride(subj.id, override)
                                .then(updated => {
                                  setSubjects(prev => prev.map(row => row.id === subj.id ? { ...row, ...updated } : row))
                                })
                                .catch((err: unknown) => {
                                  setError(extractErrorMessage(err, { loadError: 'Einstellung konnte nicht gespeichert werden' }))
                                })
                            }}
                            aria-label={`${subj.name} Qualifikation`}
                            className="block w-[6.5rem] border rounded px-1 py-0.5 text-[10px] bg-white"
                          >
                            <option value="cells">«wie bisher»</option>
                            <option value="all">Alle</option>
                            <option value="none">Keiner</option>
                          </select>
                          </div>
                          </div>
                        </th>
                      ))}
                      <th className="p-2 bg-gray-100 border text-center w-12">
                        <button
                          onClick={() => setNewSubjectMode(true)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-lg"
                          title="Neues Subject"
                        >
                          +
                        </button>
                      </th>
                    </tr>
                    {newSubjectMode && (
                      <tr>
                        <td className="p-2 bg-gray-50 border sticky left-0 bg-gray-50 z-10"></td>
                        <td colSpan={subjects.length + 1} className="p-2 border">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newSubjectName}
                              onChange={(e) => setNewSubjectName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreateSubject()
                                if (e.key === 'Escape') { setNewSubjectMode(false); setNewSubjectName('') }
                              }}
                              placeholder="Neues Subject..."
                              className="border rounded px-2 py-1 text-sm flex-1"
                              autoFocus
                            />
                            <button onClick={handleCreateSubject} className="text-green-600 hover:text-green-800 text-sm">✓</button>
                            <button onClick={() => { setNewSubjectMode(false); setNewSubjectName('') }} className="text-red-600 hover:text-red-800 text-sm">✗</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {visibleBackgrounds.map((bg) => {
                      const unmatched = bg.teacher_count === 0
                      return (
                      <tr key={bg.id} id={`matrix-bg-${bg.id}`} className={unmatched ? 'bg-red-50' : undefined}>
                        <td className={`p-2 border font-medium sticky left-0 z-10 group ${unmatched ? 'bg-red-100 text-red-700 shadow-[inset_4px_0_0_#b91c1c]' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between gap-1">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 flex-shrink-0"
                              checked={selectedBackgroundIds.has(bg.id)}
                              onChange={() => setSelectedBackgroundIds(prev => toggleSetId(prev, bg.id))}
                              onClick={e => e.stopPropagation()}
                              title={`${bg.name} auswählen`}
                              aria-label={`${bg.name} auswählen`}
                            />
                            <span
                              className={`cursor-pointer flex items-center gap-1 ${unmatched ? 'hover:text-red-800' : 'hover:text-blue-600'}`}
                              onClick={() => handleShowAliases('background', bg.id, bg.name, bg.code)}
                              title="Aliase und Code anzeigen"
                            >
                              {bg.name}
                              {unmatched && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-200 text-red-800">kein Match</span>
                              )}
                              {(bg.code || true) && <Tag className={`w-3 h-3 ${unmatched ? 'text-red-400' : 'text-gray-400'}`} />}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteBackground(bg.id, bg.name)
                              }}
                              disabled={deletingBackgroundId === bg.id}
                              className="p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-100"
                              title="Background löschen"
                            >
                              {deletingBackgroundId === bg.id ? (
                                <span className="inline-block w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <X className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </td>
                        {subjects.map((subj) => {
                          const entry = matrix.get(bg.id)?.get(subj.id)
                          const cellGroups = (groupsBySubject.get(subj.id) || []).filter(g =>
                            g.members.some(m => m.background_id === bg.id),
                          )
                          const isEditing = editingCell?.bgId === bg.id && editingCell?.subjId === subj.id
                          const inActiveCombo = Boolean(
                            editingCell
                            && cellDraft
                            && !cellDraft.qualified
                            && editingCell.subjId === subj.id
                            && cellDraft.combos[cellDraft.activeIndex]?.memberBgIds.includes(bg.id),
                          )
                          return (
                            <td
                              key={`${bg.id}-${subj.id}`}
                              className={`p-2 border text-center ${isEditing || inActiveCombo ? 'bg-blue-50 ring-2 ring-inset ring-blue-500' : unmatched ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                            >
                              {cellGroups.length > 0 || entry ? (
                                <div className="flex items-center justify-center gap-1 group flex-wrap">
                                  {cellGroups.length > 0 ? cellGroups.map(g => (
                                    <span
                                      key={g.id}
                                      className={`inline-flex items-center gap-0.5 px-2 py-1 rounded text-xs font-medium ${getQualificationColor('group', g.name)}`}
                                    >
                                      <button
                                        type="button"
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          openCellEditor(bg.id, subj.id, e.currentTarget)
                                        }}
                                      >
                                        {groupChipLabel(g.name, g.match_mode, g.members, bg.id)}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          void handleDeleteCellGroup(g, bg.id, subj.id, entry)
                                        }}
                                        className="p-0.5 -mr-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                        title="Gruppe löschen"
                                        aria-label={`${g.name} löschen`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  )) : (
                                    <span
                                      className={`inline-flex items-center gap-0.5 px-2 py-1 rounded text-xs font-medium ${getQualificationColor(entry!.qualification_type, entry!.group_name)}`}
                                    >
                                      <button
                                        type="button"
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          openCellEditor(bg.id, subj.id, e.currentTarget)
                                        }}
                                      >
                                        {entry!.group_name || 'Qualifiziert'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          void handleDelete(bg.id, bg.name, subj.id, subj.name, entry)
                                        }}
                                        className="p-0.5 -mr-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                        title="Löschen"
                                        aria-label={`${entry!.group_name || 'Qualifiziert'} löschen`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <button
                                  className={`${unmatched ? 'text-red-300' : 'text-gray-300'} hover:text-blue-600 hover:bg-blue-50 rounded px-2 py-1 transition-colors text-lg`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openCellEditor(bg.id, subj.id, e.currentTarget)
                                  }}
                                >
                                  +
                                </button>
                              )}
                            </td>
                          )
                        })}
                        <td className="p-2 border text-center"></td>
                      </tr>
                      )
                    })}
                    <tr>
                      <td className="p-2 bg-gray-50 border font-medium sticky left-0 bg-gray-50 z-10">
                        <button
                          onClick={() => setNewBackgroundMode(true)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-lg"
                          title="Neuer Background"
                        >
                          +
                        </button>
                      </td>
                      {subjects.map((subj) => (
                        <td key={`new-bg-${subj.id}`} className="p-2 border"></td>
                      ))}
                      <td className="p-2 border"></td>
                    </tr>
                    {newBackgroundMode && (
                      <tr>
                        <td colSpan={subjects.length + 2} className="p-2 border">
                          <div className="flex items-center gap-2 max-w-xs">
                            <input
                              type="text"
                              value={newBackgroundName}
                              onChange={(e) => setNewBackgroundName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreateBackground()
                                if (e.key === 'Escape') { setNewBackgroundMode(false); setNewBackgroundName('') }
                              }}
                              placeholder="Neuer Background..."
                              className="border rounded px-2 py-1 text-sm flex-1"
                              autoFocus
                            />
                            <button onClick={handleCreateBackground} className="text-green-600 hover:text-green-800 text-sm">✓</button>
                            <button onClick={() => { setNewBackgroundMode(false); setNewBackgroundName('') }} className="text-red-600 hover:text-red-800 text-sm">✗</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {editingCell && cellDraft && (() => {
                const bg = backgrounds.find(b => b.id === editingCell.bgId)
                const subj = subjects.find(s => s.id === editingCell.subjId)
                const addCombo = () => {
                  const used = occupiedGroupNamesForSubject(
                    groups.filter(g => g.subject_id === editingCell.subjId),
                    cellDraft.combos.map(c => c.name),
                    editingCell.bgId,
                  )
                  const next = nextGroupNameForSubject(subj?.name || 'Fach', used)
                  setCellDraft(prev => {
                    if (!prev) return prev
                    const only = prev.combos.length === 1 ? prev.combos[0] : null
                    if (
                      only
                      && only.mode === 'or'
                      && only.memberBgIds.length === 1
                      && only.memberBgIds[0] === editingCell.bgId
                    ) {
                      return {
                        ...prev,
                        qualified: false,
                        activeIndex: 0,
                        combos: [{ ...only, mode: 'and' }],
                      }
                    }
                    const combos = [...prev.combos, { name: next, mode: 'and' as const, memberBgIds: [editingCell.bgId] }]
                    return { ...prev, combos, activeIndex: combos.length - 1, qualified: false }
                  })
                }
                const removeCombo = (idx: number) => {
                  setCellDraft(prev => {
                    if (!prev) return prev
                    const combos = prev.combos.filter((_, i) => i !== idx)
                    return {
                      ...prev,
                      combos,
                      activeIndex: combos.length === 0 ? 0 : Math.min(idx, combos.length - 1),
                    }
                  })
                }
                const renameCombo = (idx: number, name: string) => {
                  setCellDraft(prev => {
                    if (!prev || prev.combos[idx]?.name === name) return prev
                    if (prev.combos.some((c, i) => i !== idx && c.name === name)) return prev
                    const existing = groups.find(g => g.subject_id === editingCell.subjId && g.name === name)
                    return {
                      ...prev,
                      combos: prev.combos.map((c, i) => {
                        if (i !== idx) return c
                        if (!existing) return { ...c, name }
                        const memberBgIds = [...new Set([
                          editingCell.bgId,
                          ...existing.members.map(m => m.background_id),
                        ])]
                        return { name, mode: existing.match_mode || 'or', memberBgIds }
                      }),
                    }
                  })
                }
                const memberLabel = (id: string) => backgrounds.find(b => b.id === id)?.code || backgrounds.find(b => b.id === id)?.name || id
                const comboChoices = (combo: CellCombo, idx: number) => {
                  const used = occupiedGroupNamesForSubject(
                    groups.filter(g => g.subject_id === editingCell.subjId),
                    cellDraft.combos.filter((_, i) => i !== idx).map(c => c.name),
                    editingCell.bgId,
                  )
                  const taken = new Set(cellDraft.combos.filter((_, i) => i !== idx).map(c => c.name))
                  const choices = groupChoicesForSubject(subj?.name || 'Fach', used)
                    .filter(n => !taken.has(n) || n === combo.name)
                  if (!choices.includes(combo.name)) choices.unshift(combo.name)
                  return choices
                }
                return (
                  <aside
                    ref={cellPanelRef}
                    className="bg-white border rounded-lg shadow-xl p-4 flex flex-col"
                    style={{
                      position: 'fixed',
                      top: cellPanelPos.top,
                      left: cellPanelPos.left,
                      width: cellPanelPos.width,
                      maxHeight: cellPanelPos.maxHeight,
                      zIndex: 50,
                    }}
                  >
                    <div className="text-[11px] text-gray-500 uppercase tracking-wide">Zelle</div>
                    <div className="font-semibold mb-1">{bg?.name || 'Hintergrund'} × {subj?.name || 'Fach'}</div>
                    <p className="text-xs text-gray-500 mb-3">Gruppe = ODER. + Neue Kombi = UND (alle Hintergründe nötig).</p>
                    <div data-cell-panel-body className="space-y-2 flex-1 overflow-y-auto min-h-0">
                      {!cellDraft.qualified && cellDraft.combos.map((combo, idx) => (
                        <div
                          key={combo.name}
                          role="button"
                          tabIndex={0}
                          className={`w-full text-left rounded border p-2 cursor-pointer ${idx === cellDraft.activeIndex ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                          onClick={() => setCellDraft(prev => prev ? { ...prev, activeIndex: idx } : prev)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <select
                              aria-label={`${combo.name} wählen`}
                              className={`text-xs font-medium rounded px-1 py-0.5 border ${getQualificationColor('group', combo.name)}`}
                              value={combo.name}
                              onClick={e => e.stopPropagation()}
                              onChange={e => renameCombo(idx, e.target.value)}
                            >
                              {comboChoices(combo, idx).map(name => (
                                <option key={name} value={name}>{name}</option>
                              ))}
                            </select>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${combo.mode === 'and' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}>
                                {combo.mode === 'and' ? 'UND' : 'ODER'}
                              </span>
                              <button
                                type="button"
                                className="p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                title="Gruppe löschen"
                                aria-label={`${combo.name} entfernen`}
                                onClick={e => {
                                  e.stopPropagation()
                                  removeCombo(idx)
                                }}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {combo.memberBgIds.map(memberLabel).join(combo.mode === 'and' ? ' ∧ ' : ' ∨ ') || '—'}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2" onClick={e => e.stopPropagation()}>
                            {backgrounds.filter(b => b.id !== editingCell.bgId).map(b => {
                              const on = combo.memberBgIds.includes(b.id)
                              return (
                                <button
                                  type="button"
                                  key={b.id}
                                  className={`text-[11px] px-1.5 py-0.5 rounded border ${on ? 'border-emerald-700 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}
                                  onClick={() => {
                                    setCellDraft(prev => {
                                      if (!prev) return prev
                                      return {
                                        ...prev,
                                        activeIndex: idx,
                                        combos: prev.combos.map((c, i) => {
                                          if (i !== idx) return c
                                          const memberBgIds = on
                                            ? c.memberBgIds.filter(id => id !== b.id)
                                            : [...c.memberBgIds, b.id]
                                          return { ...c, memberBgIds }
                                        }),
                                      }
                                    })
                                  }}
                                >
                                  {b.code || b.name}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="w-full text-left text-sm text-blue-600 hover:text-blue-800 py-1"
                        onClick={addCombo}
                      >
                        + Neue Kombi
                      </button>
                      <label className="flex items-center gap-2 pt-2 border-t">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={cellDraft.qualified}
                          onChange={() => {
                            setCellDraft(prev => prev ? {
                              ...prev,
                              qualified: !prev.qualified,
                            } : prev)
                          }}
                        />
                        <span className={`text-sm px-1 rounded ${getQualificationColor('qualified', null)}`}>Qualifiziert</span>
                      </label>
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                      <button type="button" className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50" onClick={closeCellEditor}>
                        Schliessen
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        onClick={() => { void applyCellPanel() }}
                        disabled={savingCell || !cellPanelDirty}
                      >
                        Übernehmen
                      </button>
                    </div>
                  </aside>
                )
              })()}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-4">Noch keine Qualifikationseinträge vorhanden</p>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={() => setActiveTab('import')}
                >
                  Matrix importieren
                </button>
              </div>
            )
          )}

          {activeTab === 'aliases' && (
            <div className="space-y-6">
              {/* Filter */}
              <div className="flex gap-4 items-center flex-wrap">
                <input
                  type="text"
                  value={aliasFilter}
                  onChange={(e) => setAliasFilter(e.target.value)}
                  placeholder="Filter nach Name oder Alias..."
                  className="border rounded px-3 py-2 text-sm flex-1 max-w-md"
                />
                <button
                  type="button"
                  className="px-3 py-2 border rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                  disabled={visibleAliasCount === 0}
                  onClick={() => {
                    if (allVisibleAliasesSelected) {
                      setSelectedSubjectAliasIds(new Set())
                      setSelectedBackgroundAliasIds(new Set())
                    } else {
                      setSelectedSubjectAliasIds(prev => {
                        const next = new Set(prev)
                        filteredSubjectAliases.forEach(a => next.add(a.id))
                        return next
                      })
                      setSelectedBackgroundAliasIds(prev => {
                        const next = new Set(prev)
                        filteredBackgroundAliases.forEach(a => next.add(a.id))
                        return next
                      })
                    }
                  }}
                >
                  {allVisibleAliasesSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
                </button>
                <button
                  type="button"
                  className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedAliasCount === 0 || deletingSelected}
                  onClick={handleDeleteSelectedAliases}
                >
                  <Trash2 className="w-4 h-4" />
                  Ausgewählte löschen{selectedAliasCount > 0 ? ` (${selectedAliasCount})` : ''}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setNewAliasMode('subject')
                      setNewAliasEntityId(subjects[0]?.id || '')
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Fach-Alias
                  </button>
                  <button
                    onClick={() => {
                      setNewAliasMode('background')
                      setNewAliasEntityId(backgrounds[0]?.id || '')
                    }}
                    className="px-3 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Background-Alias
                  </button>
                </div>
              </div>

              {/* New Alias Form */}
              {newAliasMode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-medium mb-3">
                    {newAliasMode === 'subject' ? 'Neues Fach-Alias' : 'Neues Background-Alias'}
                  </h3>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-sm text-gray-600 mb-1">
                        {newAliasMode === 'subject' ? 'Fach' : 'Background'}
                      </label>
                      <select
                        value={newAliasEntityId}
                        onChange={(e) => setNewAliasEntityId(e.target.value)}
                        className="w-full border rounded px-3 py-2 text-sm"
                      >
                        {newAliasMode === 'subject'
                          ? subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                          : backgrounds.map(b => <option key={b.id} value={b.id}>{b.name}</option>)
                        }
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm text-gray-600 mb-1">Alias Name</label>
                      <input
                        type="text"
                        value={newAliasName}
                        onChange={(e) => setNewAliasName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (newAliasMode === 'subject') handleCreateSubjectAlias()
                            else handleCreateBackgroundAlias()
                          }
                          if (e.key === 'Escape') {
                            setNewAliasMode(null)
                            setNewAliasName('')
                            setNewAliasEntityId('')
                          }
                        }}
                        placeholder="z.B. Mathe für Mathematik"
                        className="w-full border rounded px-3 py-2 text-sm"
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={newAliasMode === 'subject' ? handleCreateSubjectAlias : handleCreateBackgroundAlias}
                        className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        Speichern
                      </button>
                      <button
                        onClick={() => {
                          setNewAliasMode(null)
                          setNewAliasName('')
                          setNewAliasEntityId('')
                        }}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Subject Aliases Section */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Fach-Aliases ({allSubjectAliases.length})
                </h2>
                {allSubjectAliases.length === 0 ? (
                  <p className="text-gray-500 text-sm">Keine Fach-Aliases vorhanden</p>
                ) : (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600"
                              checked={allVisibleSubjectAliasesSelected}
                              ref={el => {
                                if (el) {
                                  el.indeterminate =
                                    filteredSubjectAliases.some(a => selectedSubjectAliasIds.has(a.id)) &&
                                    !allVisibleSubjectAliasesSelected
                                }
                              }}
                              onChange={() => {
                                if (allVisibleSubjectAliasesSelected) {
                                  setSelectedSubjectAliasIds(prev => {
                                    const next = new Set(prev)
                                    filteredSubjectAliases.forEach(a => next.delete(a.id))
                                    return next
                                  })
                                } else {
                                  setSelectedSubjectAliasIds(prev => {
                                    const next = new Set(prev)
                                    filteredSubjectAliases.forEach(a => next.add(a.id))
                                    return next
                                  })
                                }
                              }}
                              title="Alle Fach-Aliase auswählen"
                              aria-label="Alle Fach-Aliase auswählen"
                            />
                          </th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Fach</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Alias</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Quelle</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-700 w-20">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredSubjectAliases
                          .map(alias => (
                            <tr key={alias.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600"
                                  checked={selectedSubjectAliasIds.has(alias.id)}
                                  onChange={() => setSelectedSubjectAliasIds(prev => toggleSetId(prev, alias.id))}
                                  aria-label={`${alias.alias_name} auswählen`}
                                />
                              </td>
                              <td className="px-4 py-3 font-medium">{alias.canonical_subject_name}</td>
                              <td className="px-4 py-3 font-mono text-gray-600">{alias.alias_name}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  alias.source_type === 'manual' ? 'bg-blue-100 text-blue-700' :
                                  alias.is_confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {alias.source_type === 'manual' ? 'Manuell' :
                                   alias.is_confirmed ? 'Bestätigt' : 'Vorschlag'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleDeleteSubjectAlias(alias.id)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                  title="Alias löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Background Aliases Section */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Background-Aliases ({allBackgroundAliases.length})
                </h2>
                {allBackgroundAliases.length === 0 ? (
                  <p className="text-gray-500 text-sm">Keine Background-Aliases vorhanden</p>
                ) : (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600"
                              checked={allVisibleBackgroundAliasesSelected}
                              ref={el => {
                                if (el) {
                                  el.indeterminate =
                                    filteredBackgroundAliases.some(a => selectedBackgroundAliasIds.has(a.id)) &&
                                    !allVisibleBackgroundAliasesSelected
                                }
                              }}
                              onChange={() => {
                                if (allVisibleBackgroundAliasesSelected) {
                                  setSelectedBackgroundAliasIds(prev => {
                                    const next = new Set(prev)
                                    filteredBackgroundAliases.forEach(a => next.delete(a.id))
                                    return next
                                  })
                                } else {
                                  setSelectedBackgroundAliasIds(prev => {
                                    const next = new Set(prev)
                                    filteredBackgroundAliases.forEach(a => next.add(a.id))
                                    return next
                                  })
                                }
                              }}
                              title="Alle Background-Aliase auswählen"
                              aria-label="Alle Background-Aliase auswählen"
                            />
                          </th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Background</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Alias</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Quelle</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-700 w-20">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredBackgroundAliases
                          .map(alias => (
                            <tr key={alias.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600"
                                  checked={selectedBackgroundAliasIds.has(alias.id)}
                                  onChange={() => setSelectedBackgroundAliasIds(prev => toggleSetId(prev, alias.id))}
                                  aria-label={`${alias.alias_name} auswählen`}
                                />
                              </td>
                              <td className="px-4 py-3 font-medium">{alias.canonical_background_name}</td>
                              <td className="px-4 py-3 font-mono text-gray-600">{alias.alias_name}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  alias.source_type === 'manual' ? 'bg-blue-100 text-blue-700' :
                                  alias.is_confirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {alias.source_type === 'manual' ? 'Manuell' :
                                   alias.is_confirmed ? 'Bestätigt' : 'Vorschlag'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleDeleteBackgroundAlias(alias.id)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                  title="Alias löschen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'import' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium mb-4">Qualifikations-Matrix importieren</h3>
              <p className="text-sm text-gray-500 mb-4">
                Lade eine Excel-Datei mit der Qualifikations-Matrix hoch.
                X, ✓, + = Qualifiziert (exakter Background). Zahlen (2-98) oder Buchstaben (B-Z) = Gruppen.
                Ohne Marker reichen Zeilen und Spalten für den Import von Hintergründen und Fächern.
              </p>
              <FileUploadSection
                onFileUploaded={(fileId) => setUploadedFileId(fileId)}
              />
              {uploadedFileId && (
                <QualificationMatrixImport
                  uploadedFileId={uploadedFileId}
                  onImportComplete={(result) => {
                    setImportResult(result)
                    loadData()
                  }}
                />
              )}
              {importResult && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">Import abgeschlossen</p>
                  <p className="text-green-700 text-sm">
                    {importResult.entries_created} neue, {importResult.entries_updated} aktualisierte Einträge
                  </p>
                  {(importResult.backgrounds_created > 0 || importResult.subjects_created > 0) && (
                    <p className="text-green-600 text-xs mt-1">
                      {importResult.backgrounds_created} Hintergründe, {importResult.subjects_created} Fächer angelegt.
                    </p>
                  )}
                  {importResult.groups_created > 0 && (
                    <p className="text-green-600 text-xs mt-1">
                      {importResult.groups_created} neue Gruppen angelegt.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showDeleteAllDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-lg font-bold">Matrix löschen</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Die Zellen der Qualifikationsmatrix löschen? Fächer und ihre Einstellung bleiben erhalten.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteAllDialog(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteAll}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {showAliasDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAliasDialog(null)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">
              {showAliasDialog.type === 'subject' ? 'Subject' : 'Background'}: {showAliasDialog.name}
            </h3>
            <div className="space-y-3">
              {showAliasDialog.code && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Code:</span>
                  <div className="mt-1 px-3 py-2 bg-gray-50 rounded font-mono text-sm">{showAliasDialog.code}</div>
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-gray-500">Name:</span>
                <div className="mt-1 px-3 py-2 bg-gray-50 rounded text-sm">{showAliasDialog.name}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Aliase ({showAliasDialog.type === 'subject' ? subjectAliases.length : backgroundAliases.length}):</span>
                <div className="mt-1 space-y-1">
                  {(showAliasDialog.type === 'subject' ? subjectAliases : backgroundAliases).length === 0 ? (
                    <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-500">Keine Aliase vorhanden</div>
                  ) : (
                    (showAliasDialog.type === 'subject' ? subjectAliases : backgroundAliases).map(alias => (
                      <div key={alias.id} className="px-3 py-2 bg-gray-50 rounded text-sm flex items-center justify-between">
                        <span className="font-mono">{alias.alias_name}</span>
                        <span className="text-xs text-gray-400">{alias.source_type || 'manual'}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowAliasDialog(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FileUploadSection({ onFileUploaded }: { onFileUploaded: (fileId: string) => void }) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('file_category', 'qualification_matrix')
      const data = await handleApiRequest<{ id: string }>(
        apiClient.post('/api/v1/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      )
      onFileUploaded(data.id)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Upload fehlgeschlagen' }))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium mb-2">Excel-Datei hochladen (.xlsx, .xls)</label>
      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={handleUpload}
        disabled={uploading}
        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer"
      />
      {uploading && <p className="text-sm text-gray-500 mt-2">Upload läuft...</p>}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  )
}
